import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  onSnapshot,
  addDoc,
  doc,
  setDoc,
  getDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import { getAuth } from "firebase/auth";

/** ================= Helpers ================= */
const fieldStyle = {
  width: "100%",
  padding: "8px 10px",
  borderRadius: 6,
  border: "1px solid #cbd5e1",
  fontSize: 14,
  boxSizing: "border-box",
};
const th = { border: "1px solid #cbd5e1", padding: 10, textAlign: "left", fontSize: 14, background: "#334155", color: "#fff" };
const td = { border: "1px solid #cbd5e1", padding: 10, fontSize: 13 };

const fmtDate = (ts) => {
  if (!ts) return "-";
  if (ts?.seconds) return new Date(ts.seconds * 1000).toLocaleString("tr-TR");
  try { return new Date(ts).toLocaleString("tr-TR"); } catch { return "-"; }
};

// Türkçe uyumlu slug (başlığı normalize edip id olarak kullanacağız)
const toSlugTR = (str = "") => {
  const tr = { 'Ç':'c','Ö':'o','Ş':'s','İ':'i','I':'i','Ğ':'g','Ü':'u','ç':'c','ö':'o','ş':'s','ı':'i','ğ':'g','ü':'u' };
  const rep = (str || "").replace(/[ÇÖŞİIĞÜçöşığü]/g, ch => tr[ch] || ch);
  return rep
    .toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
};

/** ================= Autocomplete (string list) ================= */
const Autocomplete = ({ value, onCommit, options, placeholder = "Seçiniz..." }) => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState(value || "");
  const wrapRef = useRef(null);

  useEffect(() => { setInput(value || ""); }, [value]);

  useEffect(() => {
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const filtered = useMemo(() => {
    const s = (input || "").toLowerCase();
    if (!s) return options.slice(0, 60);
    return options.filter(o => (o || "").toLowerCase().includes(s)).slice(0, 60);
  }, [input, options]);

  const onKeyDown = (e) => {
    if (e.key === "Enter" && filtered.length === 1) {
      onCommit(filtered[0]);
      setInput(filtered[0]);
      setOpen(false);
    }
  };

  return (
    <div style={{ position: "relative" }} ref={wrapRef}>
      <input
        value={input}
        onChange={(e) => { setInput(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        style={fieldStyle}
      />
      {open && filtered.length > 0 && (
        <ul
          style={{
            position: "absolute",
            zIndex: 50,
            listStyle: "none",
            margin: "4px 0 0 0",
            padding: 0,
            width: "100%",
            maxHeight: 240,
            overflowY: "auto",
            background: "#fff",
            border: "1px solid #cbd5e1",
            borderRadius: 6,
            boxShadow: "0 4px 12px rgba(0,0,0,0.06)"
          }}
        >
          {filtered.map((opt, i) => (
            <li
              key={`${opt}-${i}`}
              onMouseDown={(e) => { e.preventDefault(); onCommit(opt); setInput(opt); setOpen(false); }}
              style={{ padding: "8px 10px", cursor: "pointer" }}
            >
              {opt}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/** ================= Page ================= */
const TerminAssign = () => {
  const auth = getAuth();
  const userEmail = auth.currentUser?.email || "-";

  // Veri
  const [issues, setIssues] = useState([]);       // tüm issues (açık + kapalı)
  const [terminMap, setTerminMap] = useState({}); // termin kategorileri {id: {...}}
  const [titleMap, setTitleMap] = useState({});   // başlık→kategori {slug: {...}}

  // Kategori formu
  const [editingCatId, setEditingCatId] = useState("");
  const [catName, setCatName] = useState("");
  const [catDays, setCatDays] = useState("");
  const [catDesc, setCatDesc] = useState("");
  const [catColor, setCatColor] = useState("#f97316");

  // Başlık atama
  const [selectedTitle, setSelectedTitle] = useState(""); // tekil başlık (string)
  const [selectedCatId, setSelectedCatId] = useState("");

  // Filtre (atanmış liste)
  const [fText, setFText] = useState("");
  const [fCategory, setFCategory] = useState("");

  // --- Tüm issues (tek listener) ---
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "issues"), (snap) => {
      setIssues(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, (err) => console.error("issues listener:", err));
    return () => unsub();
  }, []);

  // --- Termin kategorileri ---
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "termin"), (snap) => {
      const m = {};
      snap.forEach(d => { m[d.id] = { id: d.id, ...d.data() }; });
      setTerminMap(m);
    }, (err) => console.error("termin listener:", err));
    return () => unsub();
  }, []);

  // --- Başlık→Kategori eşlemesi ---
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "termin_title_map"), (snap) => {
      const m = {};
      snap.forEach(d => { m[d.id] = { id: d.id, ...d.data() }; }); // id=slug
      setTitleMap(m);
    }, (err) => console.error("title_map listener:", err));
    return () => unsub();
  }, []);

  /** -------- Tekil başlıkları çıkar (slug’lı) -------- */
  const titleStats = useMemo(() => {
    // slug -> { slug, title (en sık görünen ham), count, openCount, closedCount }
    const tmp = {};
    for (const it of issues) {
      const raw = (it.issue || "").trim();
      if (!raw) continue;
      const slug = toSlugTR(raw);
      if (!slug) continue;
      if (!tmp[slug]) tmp[slug] = { counts: {}, total: 0, open: 0, closed: 0 };
      tmp[slug].counts[raw] = (tmp[slug].counts[raw] || 0) + 1;
      tmp[slug].total += 1;
      if (it.resolved) tmp[slug].closed += 1; else tmp[slug].open += 1;
    }
    const out = {};
    Object.keys(tmp).forEach(slug => {
      const counts = tmp[slug].counts;
      const display = Object.keys(counts).sort((a,b) => counts[b] - counts[a])[0];
      out[slug] = {
        slug,
        title: display,
        count: tmp[slug].total,
        openCount: tmp[slug].open,
        closedCount: tmp[slug].closed
      };
    });
    return out;
  }, [issues]);

  // Atanmamış başlıklar (mükerrer yok, yalnızca 1 kez görünür)
  const unassignedTitleOptions = useMemo(() => {
    return Object.values(titleStats)
      .filter(t => !titleMap[t.slug])
      .map(t => t.title)
      .sort((a,b) => a.localeCompare(b, "tr"));
  }, [titleStats, titleMap]);

  // Atanmış başlıklar (alt tablo için)
  const assignedRows = useMemo(() => {
    const rows = Object.values(titleMap).map(m => {
      const stat = titleStats[m.id] || null; // m.id = slug
      const cat = m.categoryId ? terminMap[m.categoryId] : null;
      return {
        slug: m.id,
        title: m.title || stat?.title || m.id,
        categoryId: m.categoryId || "",
        categoryName: cat?.name || "-",
        categoryColor: cat?.color || "#e2e8f0",
        days: typeof cat?.days === "number" ? cat.days : "-",
        count: stat?.count ?? 0,
        openCount: stat?.openCount ?? 0,
        closedCount: stat?.closedCount ?? 0,
        updatedAt: m.updatedAt,
        updatedBy: m.updatedBy
      };
    });
    return rows
      .filter(r => !fText || r.title.toLowerCase().includes(fText.toLowerCase()))
      .filter(r => !fCategory || r.categoryId === fCategory)
      .sort((a,b) => a.title.localeCompare(b.title, "tr"));
  }, [titleMap, titleStats, terminMap, fText, fCategory]);

  const categories = useMemo(() => {
    return Object.values(terminMap).sort((a,b)=> (a.name||"").localeCompare(b.name||"","tr"));
  }, [terminMap]);

  /** ================= Kategori: oluştur / güncelle ================= */
  const resetCatForm = () => {
    setEditingCatId("");
    setCatName("");
    setCatDays("");
    setCatDesc("");
    setCatColor("#f97316");
  };

  const loadCatToForm = (id) => {
    if (!id) { resetCatForm(); return; }
    const c = terminMap[id];
    if (!c) return;
    setEditingCatId(id);
    setCatName(c.name || "");
    setCatDays(typeof c.days === "number" ? String(c.days) : "");
    setCatDesc(c.description || "");
    setCatColor(c.color || "#f97316");
  };

  const createCategory = async () => {
    if (!catName || !catDays) return alert("Kategori adı ve termin süresi zorunlu.");
    const daysNum = Number(catDays);
    if (!Number.isFinite(daysNum) || daysNum < 0) return alert("Termin süresi sayı olmalıdır.");
    try {
      await addDoc(collection(db, "termin"), {
        name: catName.trim(),
        days: daysNum,
        description: (catDesc || "").trim(),
        color: catColor || "#f97316",
        active: true,
        createdAt: serverTimestamp(),
        createdBy: userEmail,
        updatedAt: serverTimestamp(),
        updatedBy: userEmail,
        history: {
          [String(Date.now())]: {
            at: serverTimestamp(),
            by: userEmail,
            changes: { created: true, name: catName.trim(), days: daysNum, color: catColor || "#f97316" }
          }
        }
      });
      resetCatForm();
    } catch (e) {
      console.error("createCategory error:", e);
      alert("Kategori oluşturma hatası: " + (e?.message || e));
    }
  };

  const updateCategory = async () => {
    if (!editingCatId) return alert("Düzenlemek için bir kategori seçin.");
    const daysNum = Number(catDays);
    if (!catName || !Number.isFinite(daysNum)) return alert("Geçerli ad/süre zorunlu.");
    try {
      const prev = terminMap[editingCatId] || {};
      const changes = {};
      const next = {
        name: catName.trim(),
        days: daysNum,
        description: (catDesc || "").trim(),
        color: catColor || "#f97316",
      };
      ["name","days","description","color"].forEach(k => {
        if ((prev[k] ?? null) !== (next[k] ?? null)) changes[k] = { from: prev[k] ?? null, to: next[k] ?? null };
      });

      const ref = doc(db, "termin", editingCatId);
      const tsKey = String(Date.now());
      await updateDoc(ref, {
        ...next,
        updatedAt: serverTimestamp(),
        updatedBy: userEmail,
        [`history.${tsKey}`]: { at: serverTimestamp(), by: userEmail, changes }
      });
    } catch (e) {
      console.error("updateCategory error:", e);
      alert("Kategori güncelleme hatası: " + (e?.message || e));
    }
  };

  /** ================= Başlık → Kategori: atama ================= */
  const assignTitleToCategory = async () => {
    const title = (selectedTitle || "").trim();
    if (!title) return alert("Önce bir başlık seçin (örn. Oluk kilidi yok/uygunsuz).");
    if (!selectedCatId) return alert("Bir kategori seçin.");

    const slug = toSlugTR(title);
    if (!slug) return alert("Başlık slug üretilemedi.");

    try {
      const ref = doc(db, "termin_title_map", slug);
      const tsKey = String(Date.now());
      const snap = await getDoc(ref);

      if (!snap.exists()) {
        // İlk kez oluştur: setDoc (merge gerekmiyor)
        await setDoc(ref, {
          title,
          slug,
          categoryId: selectedCatId,
          createdAt: serverTimestamp(),
          createdBy: userEmail,
          updatedAt: serverTimestamp(),
          updatedBy: userEmail,
          history: {
            [tsKey]: { at: serverTimestamp(), by: userEmail, changes: { created: true, title, categoryId: selectedCatId } }
          }
        });
      } else {
        // Varsa güncelle + history
        const prev = snap.data() || {};
        await updateDoc(ref, {
          title,
          categoryId: selectedCatId,
          updatedAt: serverTimestamp(),
          updatedBy: userEmail,
          [`history.${tsKey}`]: {
            at: serverTimestamp(),
            by: userEmail,
            changes: {
              categoryId: { from: prev.categoryId || null, to: selectedCatId },
              ...(prev.title !== title ? { title: { from: prev.title || null, to: title } } : {})
            }
          }
        });
      }

      setSelectedTitle("");
      setSelectedCatId("");
    } catch (e) {
      console.error("assignTitleToCategory error:", e);
      alert("Başlık atama hatası: " + (e?.message || e));
    }
  };

  const changeMappingCategory = async (row, newCatId) => {
    try {
      const ref = doc(db, "termin_title_map", row.slug);
      const tsKey = String(Date.now());
      await updateDoc(ref, {
        categoryId: newCatId,
        updatedAt: serverTimestamp(),
        updatedBy: userEmail,
        [`history.${tsKey}`]: {
          at: serverTimestamp(),
          by: userEmail,
          changes: { categoryId: { from: row.categoryId || null, to: newCatId } }
        }
      });
    } catch (e) {
      console.error("changeMappingCategory error:", e);
      alert("Değişiklik hatası: " + (e?.message || e));
    }
  };

  const removeTitleMapping = async (slug) => {
    if (!window.confirm("Bu başlık-kategori eşlemesini kaldırmak istiyor musunuz?")) return;
    try {
      await deleteDoc(doc(db, "termin_title_map", slug));
    } catch (e) {
      console.error("removeTitleMapping error:", e);
      alert("Silme hatası: " + (e?.message || e));
    }
  };

  /** ================= UI ================= */
  return (
    <div style={{ padding: 20 }}>
      <h2 style={{ textAlign: "center", marginBottom: 12 }}>Kategori Oluştur & Başlık (Title) Bazlı Atama</h2>

      {/* Üst grid: Sol = Kategori Formu, Sağ = Başlık Atama */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
        {/* KATEGORİ FORMU */}
        <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: 12 }}>
          <h3 style={{ marginTop: 0, marginBottom: 8 }}>Kategori Oluştur / Düzenle</h3>

          <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8, marginBottom: 8 }}>
            <select
              value={editingCatId}
              onChange={(e) => loadCatToForm(e.target.value)}
              style={fieldStyle}
              title="Düzenlemek için kategori seçin (boş = yeni)"
            >
              <option value="">— Yeni Kategori —</option>
              {Object.values(terminMap).sort((a,b)=> (a.name||"").localeCompare(b.name||"","tr")).map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <button
              onClick={resetCatForm}
              style={{ padding: "8px 12px", border: "1px solid #cbd5e1", borderRadius: 6, background: "#fff", cursor: "pointer" }}
            >
              Sıfırla
            </button>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 140px", gap: 8, marginBottom: 8 }}>
            <input value={catName} onChange={(e) => setCatName(e.target.value)} placeholder="Kategori adı" style={fieldStyle}/>
            <input type="number" value={catDays} onChange={(e) => setCatDays(e.target.value)} placeholder="Termin (gün)" style={fieldStyle} min={0}/>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 160px", gap: 8, marginBottom: 8 }}>
            <input value={catDesc} onChange={(e) => setCatDesc(e.target.value)} placeholder="Açıklama" style={fieldStyle}/>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input
                type="color"
                value={catColor}
                onChange={(e) => setCatColor(e.target.value)}
                title="Kategori rengi"
                style={{ width: 46, height: 38, border: "1px solid #cbd5e1", borderRadius: 6, background: "#fff", cursor: "pointer" }}
              />
              <span style={{ fontSize: 12, color: "#475569" }}>Renk</span>
            </div>
          </div>

          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={createCategory}
              style={{ padding: "8px 12px", border: "none", borderRadius: 6, background: "#10b981", color: "#fff", cursor: "pointer" }}
            >
              Oluştur
            </button>
            <button
              onClick={updateCategory}
              style={{ padding: "8px 12px", border: "none", borderRadius: 6, background: "#3b82f6", color: "#fff", cursor: "pointer" }}
            >
              Güncelle
            </button>
          </div>
        </div>

        {/* BAŞLIK ATAMA */}
        <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: 12 }}>
          <h3 style={{ marginTop: 0, marginBottom: 8 }}>Başlık → Kategori Atama</h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 8 }}>
            <Autocomplete
              value={selectedTitle}
              onCommit={setSelectedTitle}
              options={unassignedTitleOptions} // SADECE atanmamış başlıklar (tekil)
              placeholder="Atanmamış başlık seç (örn. Oluk kilidi yok/uygunsuz)"
            />
            <select value={selectedCatId} onChange={(e) => setSelectedCatId(e.target.value)} style={fieldStyle}>
              <option value="">— Kategori seç —</option>
              {categories.map(c => (
                <option key={c.id} value={c.id}>
                  {c.name} {typeof c.days === "number" ? `(${c.days}g)` : ""}
                </option>
              ))}
            </select>
            <button
              onClick={assignTitleToCategory}
              style={{ padding: "8px 12px", border: "none", borderRadius: 6, background: "#0ea5e9", color: "#fff", cursor: "pointer" }}
            >
              Ata
            </button>
          </div>
          <p style={{ marginTop: 8, fontSize: 12, color: "#475569" }}>
            Not: Atama <b>başlığın slug’ı</b> ile tutulur; aynı başlığa sahip <b>tüm kayıtlar</b> otomatik bu kategoriye bağlanır.
          </p>
        </div>
      </div>

      {/* Atanmış başlıklar (özet tablo) */}
      <div style={{ marginTop: 8, marginBottom: 8 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 240px", gap: 8 }}>
          <input value={fText} onChange={(e)=>setFText(e.target.value)} placeholder="Başlıkta ara" style={fieldStyle}/>
          <select value={fCategory} onChange={(e)=>setFCategory(e.target.value)} style={fieldStyle}>
            <option value="">Kategori: Tümü</option>
            {categories.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 1000, fontFamily: "Arial" }}>
          <thead>
            <tr>
              <th style={th}>Başlık</th>
              <th style={th}>Kategori</th>
              <th style={th}>Termin (gün)</th>
              <th style={th}>Kayıt</th>
              <th style={th}>Açık</th>
              <th style={th}>Çözülmüş</th>
              <th style={th}>Son Güncelleme</th>
              <th style={th}>İşlem</th>
            </tr>
          </thead>
          <tbody>
            {assignedRows.length === 0 && (
              <tr><td colSpan={8} style={{ padding: 12, textAlign: "center" }}>Atanmış başlık yok</td></tr>
            )}
            {assignedRows.map(r => (
              <tr key={r.slug}>
                <td style={{ ...td, fontWeight: 600 }}>{r.title}</td>
                <td style={td}>
                  <span style={{ padding: "2px 8px", borderRadius: 999, background: r.categoryColor, color: "#fff" }}>
                    {r.categoryName}
                  </span>
                </td>
                <td style={td}>{r.days}</td>
                <td style={td}>{r.count}</td>
                <td style={td}>{r.openCount}</td>
                <td style={td}>{r.closedCount}</td>
                <td style={td}>{fmtDate(r.updatedAt)}</td>
                <td style={td}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <select
                      value={r.categoryId}
                      onChange={(e) => changeMappingCategory(r, e.target.value)}
                      style={fieldStyle}
                    >
                      {categories.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                    <button
                      onClick={() => removeTitleMapping(r.slug)}
                      style={{ padding: "6px 10px", border: "none", borderRadius: 6, background: "#ef4444", color: "#fff", cursor: "pointer" }}
                    >
                      Kaldır
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>


    </div>
  );
};

export default TerminAssign;
